import { useState, useEffect } from 'react';
import { Search, Loader2, Check, AlertCircle, Lock, Globe2, Flag, User } from 'lucide-react';
import { Field } from '../components/ui/Field';
import { Button } from '../components/ui/Button';
import {
  formatCNPJ, formatCEP, formatPhone, validateCNPJ, validateEmail,
  formatCPF, validateCPF, onlyDigits,
} from '../lib/formatters';
import { lookupCNPJ } from '../lib/cnpj';
import { ESPELHO_THEMES, ESPELHO_THEMES_ORDER } from '../lib/constants';

// Default em branco pra empresa_id forca o usuario a escolher (antes
// pegava empresas[0] e silenciosamente cadastrava na primeira da lista).
// lockedEmpresaId / lockedUnidadeId vem da replica ativa: quando o
// usuario esta dentro de uma replica, o tomador novo ja sai pre-vinculado
// aquela empresa/unidade.
const blank = (lockedEmpresaId, lockedUnidadeId) => ({
  cnpj: '', razao_social: '', nome_fantasia: '', cnae_principal: '',
  logradouro: '', numero: '', bairro: '', cidade: '', uf: '', cep: '',
  email: '', telefone: '', inscricao_municipal: '', inscricao_estadual: '',
  logo_url: '', tema_cor: 'azul',
  is_exterior: false, endereco_exterior: '',
  empresa_id: lockedEmpresaId || '',
  unidade_negocio_id: lockedUnidadeId || null,
});

// Detecta modo do cadastro pelo documento salvo. Usado ao editar
// tomador existente: se o cnpj tem 11 digitos, e Pessoa Fisica.
const detectModeFromInitial = (initial) => {
  if (!initial) return 'pj';
  if (initial.is_exterior) return 'ext';
  const d = onlyDigits(initial.cnpj || '');
  if (d.length === 11) return 'pf';
  return 'pj';
};

export function CNPJEntityForm({
  initial,
  empresas,
  isTomador,
  // Lista completa de unidades cadastradas (filtramos pela empresa atual
  // do form). Pode ser undefined no form de empresa (isTomador=false).
  unidades = [],
  // Replica ativa: trava empresa_id/unidade_negocio_id e desabilita os
  // selects no contexto da replica. Em modo principal, ambos sao null.
  lockedEmpresaId = null,
  lockedUnidadeId = null,
  isReplicaMode = false,
  // Quando true, adiciona uma 3a aba 'PF' no toggle. Serve pra cadastrar
  // tomador Pessoa Fisica (CPF) alem de PJ (CNPJ) e Exterior. So faz
  // sentido em isTomador=true — empresa emissora sempre eh PJ.
  allowPessoaFisica = false,
  onSubmit,
  onCancel,
}) {
  const [form, setForm] = useState(initial || blank(lockedEmpresaId, lockedUnidadeId));
  // Modo do cadastro: 'pj' | 'pf' | 'ext'. Guardado em estado separado
  // pra permitir 3 abas (o form no DB so tem is_exterior — PF eh
  // detectado por digito do documento). Ao editar, arranca do modo
  // certo baseado no documento salvo.
  const [mode, setMode] = useState(() => detectModeFromInitial(initial));
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [lookupMsg, setLookupMsg] = useState(null);

  const isPF = mode === 'pf';
  const isExterior = mode === 'ext';
  const isPJ = mode === 'pj';

  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // Sincroniza is_exterior com o modo (o DB so persiste esse boolean).
  // PF fica com is_exterior=false — a distincao PJ/PF vem do documento.
  useEffect(() => {
    setForm(f => ({ ...f, is_exterior: isExterior }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  // Ao trocar de modo, reformata o documento se as regras mudarem.
  // PJ→PF: recorta pra 11 digitos e aplica mascara CPF.
  // PF→PJ: aplica mascara CNPJ (aceita ate 14).
  // Exterior: deixa livre.
  useEffect(() => {
    setForm(f => {
      if (isExterior) return f;
      if (isPF) return { ...f, cnpj: formatCPF(f.cnpj) };
      return { ...f, cnpj: formatCNPJ(f.cnpj) };
    });
    setErrors({});
    setLookupMsg(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  // Em modo principal: se o usuario muda a empresa, e a unidade selecionada
  // nao pertence mais a essa empresa, limpa pra evitar dado inconsistente.
  useEffect(() => {
    if (isReplicaMode) return;
    if (!form.unidade_negocio_id) return;
    const u = unidades.find(x => x.id === form.unidade_negocio_id);
    if (!u || u.empresa_id !== form.empresa_id) {
      update('unidade_negocio_id', null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.empresa_id]);

  // Unidades disponiveis no select do principal: somente as da empresa
  // selecionada (caso contrario o usuario veria unidades de outra empresa).
  const unidadesDaEmpresa = isTomador && form.empresa_id
    ? unidades.filter(u => u.empresa_id === form.empresa_id)
    : [];

  const handleLookup = async () => {
    if (!validateCNPJ(form.cnpj)) {
      setErrors(e => ({ ...e, cnpj: 'CNPJ inválido' }));
      return;
    }
    setErrors({});
    setLoading(true);
    setLookupMsg(null);
    const res = await lookupCNPJ(form.cnpj);
    setLoading(false);
    if (res.ok) {
      setForm(f => ({ ...f, ...res.data }));
      setLookupMsg({ type: 'ok', text: 'Dados preenchidos automaticamente. Confira e complete IM/IE se necessário.' });
    } else {
      setLookupMsg({ type: 'err', text: res.error });
    }
  };

  const handleSubmit = () => {
    const e = {};
    if (isExterior) {
      // Modo exterior: nome obrigatorio, endereco obrigatorio,
      // identificador opcional (sem validacao de CNPJ).
      if (!form.razao_social.trim()) e.razao_social = 'Obrigatório';
      if (!(form.endereco_exterior || '').trim()) e.endereco_exterior = 'Obrigatório';
    } else if (isPF) {
      // Modo PF: CPF valido obrigatorio, nome obrigatorio.
      if (!validateCPF(form.cnpj)) e.cnpj = 'CPF inválido';
      if (!form.razao_social.trim()) e.razao_social = 'Obrigatório';
    } else {
      // Modo PJ (Brasil): CNPJ valido obrigatorio.
      if (!validateCNPJ(form.cnpj)) e.cnpj = 'CNPJ inválido';
      if (!form.razao_social.trim()) e.razao_social = 'Obrigatório';
    }
    if (form.email && !validateEmail(form.email)) e.email = 'E-mail inválido';
    // empresa_id e opcional pra tomador: vazio significa "cliente em todas
    // as empresas". A unica validacao e: se estiver dentro de replica
    // bloqueada, o lockedEmpresaId tem que estar presente (forma de
    // contrato — se ta locked, deveria sempre ter id).
    setErrors(e);
    if (Object.keys(e).length === 0) onSubmit(form);
  };

  // Abas do toggle. Empresa (isTomador=false) e replicas sem
  // allowPessoaFisica ficam com 2 abas (Brasil/Exterior). Tomador em
  // replica com a flag ligada ganha uma 3a aba 'PF' entre Brasil e
  // Exterior.
  const showPF = allowPessoaFisica && isTomador;
  const modeTabs = showPF
    ? [
        { key: 'pj',  label: 'Brasil (PJ)', icon: Flag },
        { key: 'pf',  label: 'Pessoa Física', icon: User },
        { key: 'ext', label: 'Exterior', icon: Globe2 },
      ]
    : [
        { key: 'pj',  label: 'Brasil',   icon: Flag },
        { key: 'ext', label: 'Exterior', icon: Globe2 },
      ];

  return (
    <div>
      {/* Toggle Brasil / (PF) / Exterior */}
      <div style={{
        display: 'inline-flex', alignItems: 'center',
        background: 'var(--surface-2)', border: '1px solid var(--border)',
        borderRadius: 'var(--radius)', padding: 4, marginBottom: 16,
      }}>
        {modeTabs.map(({ key, label, icon: Icon }) => {
          const active = mode === key;
          return (
            <button
              key={key}
              type="button"
              onClick={() => setMode(key)}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '6px 14px', fontSize: 12.5, fontWeight: 600,
                border: 'none', borderRadius: 'calc(var(--radius) - 2px)',
                cursor: 'pointer', fontFamily: 'inherit',
                background: active ? 'var(--surface)' : 'transparent',
                color: active ? 'var(--primary)' : 'var(--muted)',
                boxShadow: active ? 'var(--shadow-sm)' : 'none',
                transition: 'all .15s ease',
              }}
            >
              <Icon size={13} strokeWidth={2.2} />
              {label}
            </button>
          );
        })}
      </div>

      {isPJ && (
        <div style={{
          background: 'var(--surface-2)', border: '1px dashed var(--border-2)',
          padding: 14, borderRadius: 'var(--radius)', marginBottom: 20,
        }}>
          <Field label="CNPJ" required error={errors.cnpj}
            helper="Digite o CNPJ e clique em buscar para preencher automaticamente">
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                className={`input ${errors.cnpj ? 'error' : ''}`}
                placeholder="00.000.000/0000-00"
                value={form.cnpj}
                onChange={e => update('cnpj', formatCNPJ(e.target.value))}
                style={{ flex: 1 }}
              />
              <Button variant="secondary" onClick={handleLookup} disabled={loading}
                icon={loading ? Loader2 : Search}>
                {loading ? 'Buscando' : 'Buscar'}
              </Button>
            </div>
          </Field>
          {lookupMsg && (
            <div style={{
              marginTop: 10, fontSize: 12.5, padding: '8px 10px', borderRadius: 8,
              background: lookupMsg.type === 'ok' ? 'var(--primary-soft)' : 'var(--danger-soft)',
              color: lookupMsg.type === 'ok' ? 'var(--primary)' : 'var(--danger)',
              border: `1px solid ${lookupMsg.type === 'ok' ? 'var(--primary-line)' : '#fda29b'}`,
              display: 'flex', alignItems: 'center', gap: 8,
            }}>
              {lookupMsg.type === 'ok' ? <Check size={14} /> : <AlertCircle size={14} />}
              {lookupMsg.text}
            </div>
          )}
        </div>
      )}

      {isPF && (
        <div style={{
          background: 'var(--surface-2)', border: '1px dashed var(--border-2)',
          padding: 14, borderRadius: 'var(--radius)', marginBottom: 20,
        }}>
          <Field label="CPF" required error={errors.cnpj}
            helper="Cadastro sem consulta automática — preencha os dados do cliente manualmente">
            <input
              className={`input ${errors.cnpj ? 'error' : ''}`}
              placeholder="000.000.000-00"
              value={form.cnpj}
              onChange={e => update('cnpj', formatCPF(e.target.value))}
            />
          </Field>
        </div>
      )}

      {isExterior && (
        <div style={{
          background: 'var(--surface-2)', border: '1px dashed var(--border-2)',
          padding: 14, borderRadius: 'var(--radius)', marginBottom: 20,
        }}>
          <Field
            label="Identificador (opcional)"
            helper="EIN, VAT, código local… use o que fizer sentido pra essa empresa"
          >
            <input
              className="input"
              placeholder="Ex.: VAT GB123456789"
              value={form.cnpj}
              onChange={e => update('cnpj', e.target.value)}
            />
          </Field>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <Field
          label={isPF ? 'Nome completo' : (isExterior ? 'Nome da empresa' : 'Razão social')}
          required
          error={errors.razao_social}
          className="col-span-2"
        >
          <input className={`input ${errors.razao_social ? 'error' : ''}`}
            value={form.razao_social} onChange={e => update('razao_social', e.target.value)} />
        </Field>

        {isPJ && (
          <Field label="Nome fantasia">
            <input className="input" value={form.nome_fantasia} onChange={e => update('nome_fantasia', e.target.value)} />
          </Field>
        )}

        {isPJ && (
          <Field label="CNAE principal" className="col-span-2" >
            <input className="input" value={form.cnae_principal} onChange={e => update('cnae_principal', e.target.value)} />
          </Field>
        )}

        {isExterior && (
          <Field
            label="Endereço completo"
            required
            error={errors.endereco_exterior}
            className="col-span-2"
            helper="Endereço inteiro em uma linha — rua, número, bairro, cidade, país, ZIP/postcode"
          >
            <input
              className={`input ${errors.endereco_exterior ? 'error' : ''}`}
              value={form.endereco_exterior || ''}
              onChange={e => update('endereco_exterior', e.target.value)}
              placeholder="221B Baker Street, Marylebone, London NW1 6XE, United Kingdom"
            />
          </Field>
        )}

        {isTomador && (
          <Field
            label="Empresa vinculada"
            error={errors.empresa_id}
            helper={
              isReplicaMode
                ? 'Bloqueada — você está dentro de uma réplica'
                : 'Opcional · deixe em branco se este cliente atende todas as empresas do grupo'
            }
          >
            <select
              className={`select ${errors.empresa_id ? 'error' : ''}`}
              value={form.empresa_id || ''}
              onChange={e => update('empresa_id', e.target.value || null)}
              disabled={isReplicaMode}
            >
              <option value="">— Cliente em todas as empresas —</option>
              {empresas.map(e => <option key={e.id} value={e.id}>{e.nome_fantasia || e.razao_social}</option>)}
            </select>
          </Field>
        )}

        {isTomador && (() => {
          // Mostra o campo de Unidade quando:
          //  - Em replica COM unidade configurada: read-only, fixo na unidade da replica.
          //  - Em principal: editavel, opcional, lista as unidades da empresa selecionada.
          //  - Em replica SEM unidade configurada: nao mostra o campo (a empresa nao usa unidades).
          if (isReplicaMode && !lockedUnidadeId) return null;

          if (isReplicaMode && lockedUnidadeId) {
            const u = unidades.find(x => x.id === lockedUnidadeId);
            return (
              <Field label="Unidade de negócio" helper="Bloqueada pela réplica — só editável no Principal">
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '10px 12px', borderRadius: 'var(--radius)',
                  background: 'var(--surface-2)', border: '1px solid var(--border)',
                  fontSize: 13.5, color: 'var(--ink-2)',
                }}>
                  <Lock size={13} style={{ color: 'var(--muted)' }} />
                  <span>{u?.nome || '—'}</span>
                </div>
              </Field>
            );
          }

          // Principal: select editavel (unidades da empresa selecionada)
          return (
            <Field
              label="Unidade de negócio"
              helper={
                !form.empresa_id
                  ? 'Selecione uma empresa primeiro'
                  : unidadesDaEmpresa.length === 0
                    ? 'Esta empresa não possui unidades cadastradas'
                    : 'Opcional · marca a unidade dona deste tomador'
              }
            >
              <select
                className="select"
                value={form.unidade_negocio_id || ''}
                onChange={e => update('unidade_negocio_id', e.target.value || null)}
                disabled={!form.empresa_id || unidadesDaEmpresa.length === 0}
              >
                <option value="">— Sem unidade —</option>
                {unidadesDaEmpresa.map(u => (
                  <option key={u.id} value={u.id}>{u.nome}</option>
                ))}
              </select>
            </Field>
          );
        })()}

        {!isExterior && (
          <div style={{ gridColumn: '1 / -1', display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 14 }}>
            <Field label="Logradouro">
              <input className="input" value={form.logradouro} onChange={e => update('logradouro', e.target.value)} />
            </Field>
            <Field label="Número">
              <input className="input" value={form.numero} onChange={e => update('numero', e.target.value)} />
            </Field>
            <Field label="Bairro">
              <input className="input" value={form.bairro} onChange={e => update('bairro', e.target.value)} />
            </Field>
          </div>
        )}

        {!isExterior && (
          <div style={{ gridColumn: '1 / -1', display: 'grid', gridTemplateColumns: '2fr 80px 1fr', gap: 14 }}>
            <Field label="Cidade">
              <input className="input" value={form.cidade} onChange={e => update('cidade', e.target.value)} />
            </Field>
            <Field label="UF">
              <input className="input" maxLength={2} value={form.uf}
                onChange={e => update('uf', e.target.value.toUpperCase())} />
            </Field>
            <Field label="CEP">
              <input className="input" value={form.cep} onChange={e => update('cep', formatCEP(e.target.value))} />
            </Field>
          </div>
        )}

        <Field label="E-mail" error={errors.email}>
          <input className={`input ${errors.email ? 'error' : ''}`} value={form.email}
            onChange={e => update('email', e.target.value)}
            placeholder={isPF ? 'cliente@email.com' : 'contato@empresa.com.br'} />
        </Field>
        <Field label="Telefone">
          <input className="input" value={form.telefone}
            onChange={e => update('telefone', formatPhone(e.target.value))} />
        </Field>

        {isPJ && (
          <Field label="Inscrição municipal" helper="Preenchimento manual (não vem da Receita)">
            <input className="input" value={form.inscricao_municipal || ''}
              onChange={e => update('inscricao_municipal', e.target.value)}
              placeholder="—" />
          </Field>
        )}
        {isPJ && (
          <Field label="Inscrição estadual" helper="Preenchimento manual (não vem da Receita)">
            <input className="input" value={form.inscricao_estadual || ''}
              onChange={e => update('inscricao_estadual', e.target.value)}
              placeholder="ISENTO ou número" />
          </Field>
        )}

        {!isTomador && (
          <Field label="Logo da empresa & tema do espelho"
            helper="ID do Google Drive (público) ou URL completa. A cor define a paleta do PDF do espelho desta empresa."
            className="col-span-2">
            <div style={{ display: 'flex', gap: 10 }}>
              <input className="input" value={form.logo_url || ''}
                placeholder="ID do Google Drive (ex: 1hgrsU_DQ4...) ou URL completa"
                onChange={e => update('logo_url', e.target.value)}
                style={{ flex: 1 }} />
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                {ESPELHO_THEMES_ORDER.map(key => {
                  const t = ESPELHO_THEMES[key];
                  const active = (form.tema_cor || 'azul') === key;
                  return (
                    <button
                      key={key}
                      type="button"
                      title={key}
                      onClick={() => update('tema_cor', key)}
                      style={{
                        width: 28, height: 28, borderRadius: 999, padding: 0,
                        background: t.main,
                        border: active ? '3px solid var(--ink)' : '2px solid var(--border)',
                        cursor: 'pointer', boxShadow: 'var(--shadow-sm)',
                        outline: 'none',
                      }}
                    />
                  );
                })}
              </div>
            </div>
          </Field>
        )}
      </div>

      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 22 }}>
        <Button variant="secondary" onClick={onCancel}>Cancelar</Button>
        <Button onClick={handleSubmit} icon={Check}>Salvar</Button>
      </div>
    </div>
  );
}
